// /src/modules/bffTokenCache.ts
import type { Me } from "../core/me";
import { postJson } from "../core/http";
import { makeIdentityKey } from "./identityKey";

export type BffTokenCache = {
  accessToken: string;
  expiresAtUtc: string; // ISO
  identityKey: string;  // provider|tenantId|userId
};

const CACHE_KEY = "bff_token_v2";
const SKEW_MS = 15_000; // refresh a bit early

export function clearBffTokenCache() {
  try {
    sessionStorage.removeItem(CACHE_KEY);
  } catch {
    // ignore
  }
}

export function readBffTokenCache(): BffTokenCache | null {
  try {
    const raw = sessionStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const obj = JSON.parse(raw) as Partial<BffTokenCache>;
    if (!obj.accessToken || !obj.expiresAtUtc || !obj.identityKey) return null;
    return {
      accessToken: obj.accessToken,
      expiresAtUtc: obj.expiresAtUtc,
      identityKey: obj.identityKey,
    };
  } catch {
    return null;
  }
}

export function writeBffTokenCache(v: BffTokenCache) {
  try {
    sessionStorage.setItem(CACHE_KEY, JSON.stringify(v));
  } catch {
    // ignore
  }
}

export function isExpired(expiresAtUtc: string): boolean {
  const expMs = Date.parse(expiresAtUtc);
  if (!Number.isFinite(expMs)) return true;
  return expMs - SKEW_MS <= Date.now();
}

/**
 * Identity-scoped token minting:
 * - If identity changes (logout/login), cache won't be reused.
 * - If not authenticated, cache is cleared and an error is thrown.
 */
type BffTokenResponse = {
  accessToken: string;
  expiresAtUtc: string;
};

export async function getBffTokenForCurrentIdentity(me: Me): Promise<string> {
  if (!me.authenticated) {
    clearBffTokenCache();
    throw new Error("NOT_AUTHENTICATED");
  }

  const identityKey = makeIdentityKey(me);

  const cached = readBffTokenCache();
  if (cached && cached.identityKey === identityKey && !isExpired(cached.expiresAtUtc)) {
    return cached.accessToken;
  }

  const resp = await postJson<BffTokenResponse>("/v1/bff/token", {});
  const { accessToken, expiresAtUtc } = resp;

  if (!accessToken || !expiresAtUtc) {
    clearBffTokenCache();
    throw new Error("BFF_INVALID_RESPONSE");
  }

  writeBffTokenCache({ accessToken, expiresAtUtc, identityKey });
  return accessToken;
}
